/**
 * MarkdownWeBuild Component - Simplified with marked
 */
import React, { useMemo } from "react";
import styled from "styled-components";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { useBrand } from "@webuild-ai/react-auth";

import { SANITISE_CONFIG } from "@/config/sanitise";
import { useTheme } from "@/hooks/use-theme";

// Default font when no brand font is specified (matches tailwind.config)
const DEFAULT_FONT_FAMILY = '"Poppins", sans-serif';

marked.use({
  gfm: true,
  breaks: false,
  pedantic: false,
  mangle: false,
} as any);

const MarkdownContainer = styled.div<{ $isDark: boolean; $fontFamily: string }>`
  /* Base font styling for consistency - uses brand font or default Poppins */
  font-family: ${(props) => props.$fontFamily};
  font-weight: 400;
  line-height: 1.5;
  font-size: 14px;

  h1 {
    font-size: 16px;
    font-weight: bold;
    margin: 8px 0;
  }
  h2 {
    font-size: 14px;
    font-weight: bold;
    margin: 8px 0;
  }
  h3 {
    font-size: 13px;
    font-weight: bold;
    margin: 8px 0;
  }
  p {
    margin: 4px 0;
  }

  /* Unordered lists with custom bullet styling (matches PDF export) */
  ul {
    list-style-type: none;
    padding-left: 20px;
    margin: 4px 0;
  }

  ul > li {
    position: relative;
    padding-left: 14px;
  }

  ul > li::before {
    content: "•";
    position: absolute;
    left: 0;
    top: 0.1em;
    font-size: 1em;
  }

  /* Level 2 nesting */
  ul ul {
    padding-left: 16px;
    margin: 4px 0 0 0;
  }

  ul ul > li::before {
    content: "◦";
  }

  /* Level 3 nesting */
  ul ul ul {
    padding-left: 16px;
  }

  ul ul ul > li::before {
    content: "▪";
    font-size: 0.9em;
  }

  /* Level 4 nesting */
  ul ul ul ul {
    padding-left: 16px;
  }

  ul ul ul ul > li::before {
    content: "•";
    font-size: 0.9em;
  }

  /* Level 5 nesting */
  ul ul ul ul ul {
    padding-left: 16px;
  }

  ul ul ul ul ul > li::before {
    content: "◦";
    font-size: 0.9em;
  }

  /* Level 6 nesting */
  ul ul ul ul ul ul {
    padding-left: 16px;
  }

  ul ul ul ul ul ul > li::before {
    content: "▪";
    font-size: 0.85em;
  }

  ol {
    padding-left: 24px;
    margin: 4px 0;
    list-style-type: decimal;
    list-style-position: outside;
  }
  li {
    margin: 0px;
  }
  strong {
    font-weight: bold;
  }
  em {
    font-style: italic;
  }

  code {
    background-color: var(--content2);
    color: var(--content1-foreground, var(--foreground));
    padding: 2px 4px;
    border-radius: 3px;
    font-family: "Courier New", monospace;
    font-size: 0.9em;
    word-break: break-word;
  }

  pre {
    background-color: var(--content2);
    color: var(--content1-foreground, var(--foreground));
    padding: 8px;
    border-radius: 4px;
    overflow-x: auto;
    max-width: 100%;
    font-family: "Courier New", monospace;
    font-size: 0.9em;
    margin: 8px 0;
    code {
      background-color: transparent;
      padding: 0;
      border-radius: 0;
    }
  }

  table {
    width: 100%;
    min-width: 300px;
    border-collapse: collapse;
    margin: 8px 0;
    overflow-x: auto;
    display: block;
  }

  thead {
    background-color: ${(props) =>
      props.$isDark ? "var(--color-brand-primary)" : "var(--content2)"};
    color: ${(props) => (props.$isDark ? "var(--color-white)" : "inherit")};
  }

  th {
    border: 1px solid var(--divider);
    padding: 4px;
    text-align: left;
    font-weight: bold;
  }
  td {
    border: 1px solid var(--divider);
    padding: 4px;
    text-align: left;
  }

  tbody tr:nth-child(even) {
    background-color: var(--content2);
  }

  tbody tr:hover {
    background-color: var(--content3);
  }

  a {
    color: var(--color-primary-500, var(--primary-500));
    text-decoration: underline;
    &:hover {
      opacity: 0.8;
    }
  }

  blockquote {
    border-left: 4px solid
      ${(props) => (props.$isDark ? "var(--color-brand-primary)" : "var(--divider)")};
    padding-left: 16px;
    margin: 8px 0;
    color: var(--default-500);
    font-style: italic;
  }

  hr {
    border: none;
    border-top: 1px solid var(--divider);
    margin: 16px 0;
  }

  del {
    text-decoration: line-through;
    opacity: 0.7;
  }
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 8px 0;
  }
`;

interface MarkdownWeBuildProps {
  children: React.ReactNode;
}

export const MarkdownWeBuild: React.FC<MarkdownWeBuildProps> = ({ children }) => {
  const { theme } = useTheme();
  const { brand } = useBrand();
  const isDark = theme === "dark";
  const fontFamily = brand.fontFamily || DEFAULT_FONT_FAMILY;

  const html = useMemo(() => {
    const markdown = children?.toString() || "";
    const rawHtml = marked.parse(markdown) as string;
    const cleanHtml = DOMPurify.sanitize(rawHtml, SANITISE_CONFIG);

    return cleanHtml;
  }, [children]);

  return (
    <MarkdownContainer
      $fontFamily={fontFamily}
      $isDark={isDark}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};
